import { EditorState, SerializedNodes, useEditor, useNode, Node } from '@craftjs/core';
import { Property } from 'csstype';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { isRepeatableSection } from 'src/components/formEditor/uiEditor/uiEditor-helper';

export const hasSection = <T>(sections: T[] | undefined, section: T) => sections?.some((s) => s === section);

export const hexToRgba = (hex?: string, alpha = 1) => {
  if (!hex) return undefined;

  const [r, g, b] = hex.match(/\w\w/g)!.map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};

export const rgbaToHex = (color?: Property.Color) => {
  if (!color) return '#000000';

  const [r, g, b] = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',');
  const hex = `#${((1 << 24) + (parseInt(r) << 16) + (parseInt(g) << 8) + parseInt(b)).toString(16).slice(1)}`;

  return hex;
};

export const getOpacityFromRgba = (color?: Property.Color) => {
  if (!color) return 100;

  const [, , , a] = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',');
  return Math.round(+a * 100);
};

/**
 * Returns a number whose value is limited to the given range.
 *
 * Example: limit the output of this computation to between 0 and 255
 * (x * 255).clamp(0, 255)
 *
 * @param {Number} min The lower boundary of the output range
 * @param {Number} max The upper boundary of the output range
 * @returns A number in the range [min, max]
 * @type Number
 */
export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// To fix: Bug #56650 Drag and drop disabled when right settings are opened
// https://github.com/react-dnd/react-dnd-html5-backend/issues/7#issuecomment-303016329
export const ModifiedBackend = (...args: any) => {
  // @ts-ignore
  const instance = new HTML5Backend(...args);
  const original = instance.handleTopDragStart;

  // @ts-ignore
  instance.handleTopDragStart = (e, ...extraArgs) => {
    e.preventDefault = () => {};
    original(e, ...extraArgs);
  };

  return instance;
};

export const fontSizeArray = [10, 11, 12, 13, 14, 15, 16, 20, 24, 32, 36, 40, 48, 64, 96, 128];

export const MIN_FONT_SIZE = Math.min(...fontSizeArray);
export const MAX_FONT_SIZE = Math.max(...fontSizeArray);

export const isValidTextSize = (value: string | null): boolean => {
  if (value === null) return true;

  const parsedNumber = Number(value);
  const isNumberInRange = parsedNumber >= MIN_FONT_SIZE && parsedNumber <= MAX_FONT_SIZE;

  return !isNaN(parsedNumber) && isNumberInRange;
};

type DataKeyWithResolvedName = { dataKey: string; resolvedName: string };
export const getDataKeysFromLayout = (layout: SerializedNodes): DataKeyWithResolvedName[] => {
  return Object.entries(layout)
    .reduce(
      (arr, [, value]) => [
        ...arr,
        {
          dataKey: value.props.dataKey,
          //@ts-ignore
          resolvedName: value.type.resolvedName,
        } satisfies DataKeyWithResolvedName,
      ],
      [] as DataKeyWithResolvedName[],
    )
    .filter((x) => !!x.dataKey);
};

export const getNodeByDataKeyFunc = (state: EditorState) => (dataKey: string) => {
  const [, node] = Object.entries(state.nodes).find(([_, node]) => node.data.props.dataKey === dataKey) || [];
  return node;
};

export const getDependentNodesBy = (state: EditorState) => (dataKey: string) =>
  Object.entries(state.nodes)
    .filter(([_, node]) => {
      return node.data.props?.visibleIf?.includes(dataKey) || node.data.props?.enableIf?.includes(dataKey);
    })
    .map(([, node]) => node);

export const useGetAvailableNodes = (): Node[] => {
  const { query } = useEditor();
  const { id } = useNode();
  const closestRepeatableContentItemId = query
    .node(id)
    .ancestors(true)
    .find((nodeId) => query.node(nodeId).get().data.displayName === 'RepeatableSection');

  return query
    .node(closestRepeatableContentItemId ?? 'ROOT')
    .descendants(true, 'childNodes')
    .filter((nodeId) => nodeId !== id)
    .map((nodeId) => query.node(nodeId).get())
    .filter((node) => !!node.data.props.dataKey);
};

export const generateNestedDataKey = (query: any, sectionDataKey: string) => (nodeId: string) => {
  const nodeDataKey = query.node(nodeId).get().data.props.dataKey;
  if (!nodeDataKey) return undefined;
  if (isRepeatableSection(query.node(nodeId).get())) return sectionDataKey;

  const parentDataKeys = query
    .node(nodeId)
    .ancestors()
    .map((id: string) =>
      isRepeatableSection(query.node(id).get()) ? sectionDataKey : query.node(id).get().data.props.dataKey,
    )
    .filter(Boolean)
    .reverse();
  parentDataKeys.push(nodeDataKey);
  return parentDataKeys.join('.');
};
